import { Injectable } from '@angular/core';

import { ParticipantPriceStatus } from '@cds/betting-offer';
import { MessageEnvelope } from '@cds/push';
import {
    CdsPushStoreService,
    isFixtureSlimUpdateEnvelope,
    isOptionMarketEnvelope,
    isParticipantUpdateEnvelope,
    updateFixture,
    updateFixtureOptionMarkets,
    updateFixtureParticipants,
} from '@frontend/sports/content-distribution/feature';
import { WinGameFixture, createParticipantWithRacingPostData, createParticipantWithTimeFormData } from '@frontend/sports/racing/feature/models';
import { Observable, scan, switchMap } from 'rxjs';

import { HorseRacingApiService } from '../../horse-racing-shared/services/horse-racing-api.service';
import { HorsesFixtureHelper } from '../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixturePricingHelper } from '../../horse-racing-shared/services/horses-fixture-pricing-helper.service';

@Injectable()
export class FixtureUpdateService {
    constructor(
        private horseRacingService: HorseRacingApiService,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private cdsPushStoreService: CdsPushStoreService,
    ) {}

    handleFixtureUpdates(fixture: WinGameFixture): Observable<WinGameFixture> {
        return this.cdsPushStoreService.subscribeToFixture(fixture).pipe(
            scan((accumulateFixture, message) => this.updatedFixtureFromMessage(accumulateFixture, message), fixture),
            switchMap((updatedFixture) => this.updateOptionGroupsAndSets(updatedFixture)),
        );
    }

    private updateFixtureParticipants(fixture: WinGameFixture, message: MessageEnvelope): WinGameFixture {
        if (fixture.timeform) {
            return updateFixtureParticipants(fixture, message, {
                creator: createParticipantWithTimeFormData,
            });
        } else {
            return updateFixtureParticipants(fixture, message, {
                creator: createParticipantWithRacingPostData,
            });
        }
    }

    private updateFixtureSlim(fixture: WinGameFixture, message: MessageEnvelope): WinGameFixture {
        return updateFixture(fixture, message);
    }

    shouldCompleteFixtureResult(updatedFixture: WinGameFixture): boolean | undefined {
        return (
            !HorsesFixtureHelper.isTradable(updatedFixture) ||
            (!updatedFixture.isOptionBet &&
                updatedFixture.participants.flatMap((p) => p.options).every((o) => o.participantPriceStatus !== ParticipantPriceStatus.Visible)) ||
            (updatedFixture.isOptionBet && updatedFixture.addons.isRaceOff)
        );
    }

    isRelevantMessage(message: MessageEnvelope): boolean {
        return isParticipantUpdateEnvelope(message) || isFixtureSlimUpdateEnvelope(message) || isOptionMarketEnvelope(message);
    }

    private updateOptionGroupsAndSets(fixture: WinGameFixture): Observable<WinGameFixture> {
        return this.horseRacingService.updateFixture(fixture);
    }

    private updatedFixtureFromMessage(fixture: WinGameFixture, message: MessageEnvelope): WinGameFixture {
        if (!this.isRelevantMessage(message) || message.payload.fixtureId !== fixture.id) {
            return fixture;
        }

        if (isParticipantUpdateEnvelope(message)) {
            fixture = this.updateFixtureParticipants(fixture, message);
        }

        if (isOptionMarketEnvelope(message)) {
            fixture = updateFixtureOptionMarkets(fixture, message);
        }

        if (isFixtureSlimUpdateEnvelope(message)) {
            fixture = this.updateFixtureSlim(fixture, message);
        }

        return fixture;
    }
}
